import React, {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { useHistory, useLocation } from "react-router-dom";
import qs from "query-string";
import useBodyScrollability from "../../../hooks/useBodyScrollability";
import { FILTER_QUERY_PARAMS } from "@/routes/productListing/constants";
import PlpAnalytics from "@/services/analytics/main/plp";
import { SORT_LOCATION } from "@/services/analytics/main/plp/types";
import { handleFilterEvent } from "@/routes/productListing/analyticsHelper";
import FilterSidebar from "./filtersSidebar";
import FiltersPanel from "./filtersPanel";
import AssistiveFiltersSection from "../assistiveFilters";
import useAssistiveFilters from "../hooks/useAssistiveFilters";
import useSelectedByQuery from "../hooks/useSelectedByQuery";
import { useRouteSlug } from "@/hooks";
import {
  arePlpFilterSelectionsEqualByParamKeys,
  buildFacetsForListingUrlMerge,
  buildPlpQueryFromFilterSelection,
  collectCategoryQueryParamKeysFromFacets,
  collectFilterQueryParamKeysFromFacets,
  collectMoreFilterQueryParamKeysFromFacets,
  getTouchedQueryParamsFromSelection,
  listingApiSelectionFromSelectedFiltersData,
  mergeSelectedFiltersFromUrlAndListing,
  normalizeQueryParamsForCompare,
  splitSizeFacetInFacets
} from "../utils";
import {
  AssistiveFilterOption,
  Facet,
  FilterOption,
  ProductListingFiltersMobProps,
  SelectedFiltersType
} from "../types";
import "./style.scss";

export const selectedFiltersContext = createContext<{
  selectedFilters: {
    [key: string]: Facet & { selectedOptions: FilterOption[] };
  };
  setSelectedFilters: React.Dispatch<
    React.SetStateAction<{
      [key: string]: Facet & {
        selectedOptions: FilterOption[];
      };
    }>
  >;
  updateSelectedFilters: (selected: any) => {
    [key: string]: Facet & {
      selectedOptions: FilterOption[];
    };
  };
  onSelection: (...args) => void;
  isFiltersOpen: boolean;
  hasMobFilterSheetEdits?: boolean;
}>(null);

const ProductListingFiltersMob: FC<ProductListingFiltersMobProps> = ({
  filters,
  itemsTotal,
  appliedFilterCount,
  selectedFiltersData,
  listingPageData
}) => {
  const history = useHistory();
  const location = useLocation();
  const routeSlug = useRouteSlug();
  const assistiveFilter = useAssistiveFilters(listingPageData);
  const preparedFilters = useMemo(
    () => splitSizeFacetInFacets(filters ?? []),
    [filters]
  );

  if (!filters) return null;

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersType>(
    {}
  );
  const selectedFiltersRef = useRef<SelectedFiltersType>({});
  const selectedFiltersDataRef = useRef(selectedFiltersData);
  selectedFiltersDataRef.current = selectedFiltersData;
  const selectedByQuery = useSelectedByQuery();
  const filterQueryParamKeys = useMemo(
    () => collectFilterQueryParamKeysFromFacets(preparedFilters as any),
    [preparedFilters]
  );
  const selectedFiltersFromListingResponse = useMemo(
    () => listingApiSelectionFromSelectedFiltersData(selectedFiltersData ?? []),
    [selectedFiltersData]
  );
  const openedWithFiltersSnapshotRef = useRef<SelectedFiltersType | null>(null);
  const openedAtLocationRef = useRef<string | null>(null);
  const skipListingUrlSyncWhileSheetPendingRef = useRef(false);
  skipListingUrlSyncWhileSheetPendingRef.current =
    isFiltersOpen &&
    !arePlpFilterSelectionsEqualByParamKeys(
      selectedFilters,
      selectedFiltersFromListingResponse,
      filterQueryParamKeys
    );
  const { enableBodyScroll, disableBodyScroll } = useBodyScrollability();

  const categoryQueryParams = useMemo(
    () => collectCategoryQueryParamKeysFromFacets(filters ?? []),
    [filters]
  );

  const moreFilterQueryParamKeys = useMemo(
    () => collectMoreFilterQueryParamKeysFromFacets(preparedFilters as any),
    [preparedFilters]
  );

  const isFiltersOpenRef = useRef(false);
  isFiltersOpenRef.current = isFiltersOpen;

  /** Query params the user changed while the sheet is open (same idea as web: only those touch the URL). */
  const sheetTouchedQueryParamKeysRef = useRef(new Set<string>());

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  useEffect(() => {
    if (!isFiltersOpen) {
      enableBodyScroll();
    }
  }, [isFiltersOpen]);

  const getUpdatedQuery = useCallback(
    (
      updatedSelection: SelectedFiltersType,
      opts?: {
        touchedQueryParamKeys?: Set<string>;
        fullFilterReplace?: boolean;
      }
    ) => {
      return buildPlpQueryFromFilterSelection(
        location.pathname,
        location.search,
        updatedSelection,
        {
          filterQueryParamKeys,
          touchedQueryParamKeys:
            opts?.touchedQueryParamKeys ?? new Set<string>(),
          fullFilterReplace: opts?.fullFilterReplace ?? false
        }
      );
    },
    [location, filterQueryParamKeys]
  );

  const hasMobFilterSheetEdits = useMemo(
    () =>
      isFiltersOpen &&
      !arePlpFilterSelectionsEqualByParamKeys(
        selectedFilters,
        selectedFiltersFromListingResponse,
        filterQueryParamKeys
      ),
    [
      isFiltersOpen,
      selectedFilters,
      selectedFiltersFromListingResponse,
      filterQueryParamKeys
    ]
  );

  const updateSelectedFilters = useCallback(
    (selected: any) => {
      let updated: {
        [key: string]: Facet & { selectedOptions: FilterOption[] };
      } = { ...selectedFiltersRef.current };
      if (selected) {
        if (Array.isArray(selected)) {
          selected.forEach(item => {
            if (item.queryParam) {
              updated[item.queryParam] = item;
            }
          });
        } else {
          if (selected.queryParam) {
            updated[selected.queryParam] = selected;
          }
        }
        if (isFiltersOpenRef.current) {
          const touched = getTouchedQueryParamsFromSelection(
            selected,
            categoryQueryParams
          );
          touched.forEach(k => sheetTouchedQueryParamKeysRef.current.add(k));
        }
      }
      setSelectedFilters(selected => ({ ...selected, ...updated }));
      selectedFiltersRef.current = {
        ...selectedFiltersRef.current,
        ...updated
      };

      return updated;
    },
    [categoryQueryParams]
  );

  const onAssistiveFilterClick = (filter: AssistiveFilterOption) => {
    sheetTouchedQueryParamKeysRef.current.clear();
    setSelectedFilters({});
    history.push(`/${routeSlug}${filter.url}`);
  };

  useEffect(() => {
    if (!filters || !filters.length) {
      onFiltersClose();
    }
  }, [filters]);

  useEffect(() => {
    if (skipListingUrlSyncWhileSheetPendingRef.current) {
      return;
    }

    const facetsFromListingResponse = buildFacetsForListingUrlMerge(
      selectedFiltersDataRef.current,
      selectedByQuery
    );

    setSelectedFilters(previousSelection =>
      mergeSelectedFiltersFromUrlAndListing(
        previousSelection,
        facetsFromListingResponse,
        selectedByQuery
      )
    );
  }, [selectedByQuery, selectedFiltersData]);

  const reconcileSizeStateWithQuery = useCallback((builtQuery: string) => {
    const { query: nextQueryObj } = qs.parseUrl(builtQuery, {
      decode: false
    });
    const sizeParam = FILTER_QUERY_PARAMS.SIZE;
    const refSizeOpts = selectedFiltersRef.current[sizeParam]?.selectedOptions;
    if (!Array.isArray(refSizeOpts) || refSizeOpts.length === 0) return;

    const nextSizeRaw = nextQueryObj[sizeParam];
    if (!nextSizeRaw) {
      delete selectedFiltersRef.current[sizeParam];
      setSelectedFilters(prev => {
        if (!(sizeParam in prev)) return prev;
        const next = { ...prev };
        delete next[sizeParam];
        return next;
      });
      return;
    }

    const urlSizeKeys = new Set(
      (Array.isArray(nextSizeRaw) ? nextSizeRaw : [nextSizeRaw]).map(String)
    );
    const kept = refSizeOpts.filter(o => urlSizeKeys.has(String(o.key)));
    if (kept.length === refSizeOpts.length) return;

    if (kept.length === 0) {
      delete selectedFiltersRef.current[sizeParam];
      setSelectedFilters(prev => {
        if (!(sizeParam in prev)) return prev;
        const next = { ...prev };
        delete next[sizeParam];
        return next;
      });
    } else {
      const updatedFacet = {
        ...selectedFiltersRef.current[sizeParam],
        selectedOptions: kept
      };
      selectedFiltersRef.current[sizeParam] = updatedFacet;
      setSelectedFilters(prev => ({ ...prev, [sizeParam]: updatedFacet }));
    }
  }, []);

  const onSelection = async selected => {
    let query;
    let moreFiltersWereCleared = false;
    if (selected) {
      const updatedFilters = updateSelectedFilters(selected);
      const touched = getTouchedQueryParamsFromSelection(
        selected,
        categoryQueryParams
      );
      // When category changes, clear moreFilter params — they're subcategory-specific
      const categoryWasTouched =
        moreFilterQueryParamKeys.size > 0 &&
        [...categoryQueryParams].some(k => touched.has(k));
      if (categoryWasTouched) {
        moreFiltersWereCleared = true;
        moreFilterQueryParamKeys.forEach(k => {
          touched.add(k);
          delete updatedFilters[k];
          delete selectedFiltersRef.current[k];
        });
        setSelectedFilters(prev => {
          const next = { ...prev };
          moreFilterQueryParamKeys.forEach(k => delete next[k]);
          return next;
        });
      }
      query = getUpdatedQuery(updatedFilters, {
        touchedQueryParamKeys: touched,
        fullFilterReplace: false
      });
    } else {
      const fromListing = listingApiSelectionFromSelectedFiltersData(
        selectedFiltersDataRef.current ?? []
      );
      const touchedSnapshot = new Set(sheetTouchedQueryParamKeysRef.current);
      const keysToCompare =
        touchedSnapshot.size > 0
          ? new Set([...filterQueryParamKeys, ...touchedSnapshot])
          : filterQueryParamKeys;
      if (
        arePlpFilterSelectionsEqualByParamKeys(
          selectedFiltersRef.current,
          fromListing,
          keysToCompare
        )
      ) {
        return;
      }
      let touched = touchedSnapshot;
      if (touched.size === 0) {
        for (const key of filterQueryParamKeys) {
          if (categoryQueryParams.has(key)) continue;
          if (
            !arePlpFilterSelectionsEqualByParamKeys(
              { [key]: selectedFiltersRef.current?.[key] },
              { [key]: fromListing?.[key] },
              new Set([key])
            )
          ) {
            touched.add(key);
          }
        }
      }
      if (touched.size === 0) {
        return;
      }
      query = getUpdatedQuery(selectedFiltersRef.current, {
        touchedQueryParamKeys: touched,
        fullFilterReplace: false
      });
    }

    const currentQueryObj = qs.parse(location.search, { decode: false });
    const nextQueryObj = qs.parseUrl(query, { decode: false }).query;
    if (
      isEqual(
        normalizeQueryParamsForCompare(currentQueryObj),
        normalizeQueryParamsForCompare(nextQueryObj)
      )
    ) {
      return;
    }

    reconcileSizeStateWithQuery(query);
    history.push(query);

    if (selected) {
      getTouchedQueryParamsFromSelection(selected, categoryQueryParams).forEach(
        k => sheetTouchedQueryParamKeysRef.current.delete(k)
      );
      if (moreFiltersWereCleared) {
        moreFilterQueryParamKeys.forEach(k =>
          sheetTouchedQueryParamKeysRef.current.delete(k)
        );
      }
    } else {
      sheetTouchedQueryParamKeysRef.current.clear();
    }
  };

  const onFiltersOpen = () => {
    sheetTouchedQueryParamKeysRef.current.clear();
    openedWithFiltersSnapshotRef.current = cloneDeep(
      selectedFiltersRef.current
    );
    openedAtLocationRef.current = location.pathname + location.search;
    setIsFiltersOpen(true);
    disableBodyScroll();
  };

  const onFiltersClose = (opts?: { revertUnsavedToUrl?: boolean }) => {
    sheetTouchedQueryParamKeysRef.current.clear();
    const revertUnsavedToUrl = opts?.revertUnsavedToUrl !== false;
    if (revertUnsavedToUrl) {
      const snapshot = openedWithFiltersSnapshotRef.current;
      const originalLocation = openedAtLocationRef.current;

      if (snapshot !== null) {
        const snapshotClone = cloneDeep(snapshot);
        setSelectedFilters(snapshotClone);
        selectedFiltersRef.current = snapshotClone;
      }

      if (
        originalLocation !== null &&
        originalLocation !== location.pathname + location.search
      ) {
        history.replace(originalLocation);
      }
    }
    openedWithFiltersSnapshotRef.current = null;
    openedAtLocationRef.current = null;
    setIsFiltersOpen(false);
  };

  return (
    <>
      <div className="filters_wrapper_resp">
        <selectedFiltersContext.Provider
          value={{
            selectedFilters,
            setSelectedFilters,
            updateSelectedFilters,
            onSelection,
            isFiltersOpen,
            hasMobFilterSheetEdits
          }}
        >
          <FiltersPanel
            itemsTotal={itemsTotal}
            filters={preparedFilters as any}
            onFiltersOpen={onFiltersOpen}
            appliedFilterCount={appliedFilterCount}
          />
          <FilterSidebar
            filters={preparedFilters as any}
            isFiltersOpen={isFiltersOpen}
            onFiltersClose={onFiltersClose}
          />
        </selectedFiltersContext.Provider>
      </div>
      {assistiveFilter && (
        <AssistiveFiltersSection
          staticFilter={assistiveFilter.prev_selection}
          filters={assistiveFilter.list}
          onFilterClick={onAssistiveFilterClick}
        />
      )}
    </>
  );
};

export default ProductListingFiltersMob;
